import { batch } from 'react-redux';
import { AsyncResult, Action, ApiAction, Result } from '../../index';
import api from '../../redux-api';

// Utils
import { removeManyHoldings } from '@/utils/portfolios';
import { BOND_TAG } from '@/utils/constants';
import { getMonthEndDataOption } from '@/utils/comparison';

// Types
import { FundStoreState } from '../types/fund-store';
import Fund from '@/types/fund';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';

// TODO -- Merge with benchmark-store (CON-289)

// Initial State
const INITIAL_STATE = {
  fundsPMED: {},
  fundsRMED: {},
  fundsPQED: {},
  fundsRQED: {},
  funds: {},
};

// Actions
const FETCH_FUND_BY_ID: ApiAction = {
  type: 'connect/funds/store/fetch',
  endpoint: 'funds/:id/',
};

const FETCH_MANY_FUNDS: ApiAction = {
  type: 'connect/funds/store/fetch-many',
  endpoint: 'funds/',
};

const FETCH_BOND_BY_ID: ApiAction = {
  type: 'connect/bonds/store/fetch',
  endpoint: 'bonds/:id/',
};

const FETCH_MANY_BONDS: ApiAction = {
  type: 'connect/bonds/store/fetch-many',
  endpoint: 'bonds/',
};

const FUNDS_STORE_GET = 'connect/funds/store/get';
const FUNDS_STORE_GET_MANY = 'connect/funds/store/get-many';
const FUNDS_STORE_ADD = 'connect/funds/store/add';
const FUNDS_STORE_REMOVE_MANY = 'connect/funds/store/remove-many';

// Reducer
export default function reducer(state: FundStoreState = INITIAL_STATE, action: Action): FundStoreState {
  switch (action.type) {
    case FUNDS_STORE_ADD:
      switch (action.monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          return {
            ...state,
            fundsRMED: { ...state.fundsRMED, [action.fund._id]: action.fund },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          return {
            ...state,
            fundsPMED: { ...state.fundsPMED, [action.fund._id]: action.fund },
          };
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          return {
            ...state,
            fundsRQED: { ...state.fundsRQED, [action.fund._id]: action.fund },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          return {
            ...state,
            fundsPQED: { ...state.fundsPQED, [action.fund._id]: action.fund },
          };
        default:
          return {
            ...state,
            funds: { ...state.funds, [action.fund._id]: action.fund },
          };
      }
    case FUNDS_STORE_REMOVE_MANY:
      return {
        ...state,
        fundsRMED: removeManyHoldings(state.fundsRMED, action.ids),
        fundsPMED: removeManyHoldings(state.fundsPMED, action.ids),
        fundsRQED: removeManyHoldings(state.fundsRQED, action.ids),
        fundsPQED: removeManyHoldings(state.fundsPQED, action.ids),
        funds: removeManyHoldings(state.funds, action.ids),
      };
    default:
      return state;
  }
}

// Action Creators
/**
 * Removes fund data from store
 * @param ids Array of fund's unique identifier
 */
export const invalidateFundData =
  (ids: string[]): Result<void> =>
  dispatch => {
    dispatch({ type: FUNDS_STORE_REMOVE_MANY, ids });
  };

/**
 * Retrieve Fund data either from memory, local cache or directly from the API
 * @param id Fund's unique identifier
 */
export const getFundData =
  (id: string, errorWhiteList?: string[]): AsyncResult<Fund> =>
  (dispatch, getState) => {
    dispatch({ type: FUNDS_STORE_GET, id });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    // See if we got it in State
    let stateFund;
    switch (monthEndDataOption) {
      case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
        stateFund = getState().funds.store.fundsRMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
        stateFund = getState().funds.store.fundsPMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
        stateFund = getState().funds.store.fundsRQED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
        stateFund = getState().funds.store.fundsPQED[id];
        break;
      default:
        stateFund = getState().funds.store.funds[id];
        break;
    }
    if (stateFund) {
      return Promise.resolve(stateFund);
    }

    if (id.startsWith(BOND_TAG)) {
      return api
        .get(FETCH_BOND_BY_ID, { id, ...(monthEndDataOption && { monthEndDataOption }) }, undefined, errorWhiteList)
        .then(({ fund }: { fund: Fund }) => {
          dispatch({ type: FUNDS_STORE_ADD, fund, monthEndDataOption });
          return fund;
        });
    }

    return api.get(FETCH_FUND_BY_ID, { id, ...(monthEndDataOption && { monthEndDataOption }) }, undefined, errorWhiteList).then(({ fund }: { fund: Fund }) => {
      dispatch({ type: FUNDS_STORE_ADD, fund, monthEndDataOption });
      return fund;
    });
  };

/**
 * Retrieve multiple Funds data either from memory, local cache or directly from the API
 * @param ids Funds' unique identifiers
 */
export const getManyFundsData =
  (rawIds: string[]): AsyncResult<Fund[]> =>
  (dispatch, getState) => {
    const ids: string[] = rawIds.filter(id => id != null && id !== '');
    dispatch({ type: FUNDS_STORE_GET_MANY, ids });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const remaining = new Set(ids);
    const fundsData = [] as Fund[];

    for (const id of ids) {
      if (id !== null) {
        // See if we got it in State
        let stateFund;
        switch (monthEndDataOption) {
          case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
            stateFund = getState().funds.store.fundsRMED[id];
            break;
          case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
            stateFund = getState().funds.store.fundsPMED[id];
            break;
          case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
            stateFund = getState().funds.store.fundsRQED[id];
            break;
          case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
            stateFund = getState().funds.store.fundsPQED[id];
            break;
          default:
            stateFund = getState().funds.store.funds[id];
            break;
        }
        if (stateFund) {
          fundsData.push(stateFund);
          remaining.delete(id);
          continue;
        }
      } else {
        fundsData.push();
        remaining.delete(id);
        continue;
      }
    }

    // Fetch the rest from the API if we're missing any
    if (remaining.size) {
      const remainingfixedIncomeFunds = Array.from(remaining).filter(id => id.startsWith(BOND_TAG));
      const remainingFunds = Array.from(remaining).filter(id => !id.startsWith(BOND_TAG));

      const promises: Array<Promise<Fund[]>> = [];

      if (remainingfixedIncomeFunds.length > 0) {
        promises.push(
          api
            .get(FETCH_MANY_BONDS, { ids: remainingfixedIncomeFunds, ...(monthEndDataOption && { monthEndDataOption }) })
            .then(({ funds }: { funds: Fund[] }) => {
              batch(() => {
                for (const fund of funds) {
                  dispatch({ type: FUNDS_STORE_ADD, fund, monthEndDataOption });
                }
              });

              return fundsData.concat(funds);
            }),
        );
      }

      if (remainingFunds.length > 0) {
        promises.push(
          api
            .get(FETCH_MANY_FUNDS, { ids: remainingFunds.join('-'), ...(monthEndDataOption && { monthEndDataOption }) })
            .then(({ funds }: { funds: Fund[] }) => {
              batch(() => {
                for (const fund of funds) {
                  dispatch({ type: FUNDS_STORE_ADD, fund, monthEndDataOption });
                }
              });

              return fundsData.concat(funds);
            }),
        );
      }

      return Promise.all(promises).then((...results) => results.reduce((acc, funds) => acc.concat(...funds), [] as Fund[]));
    }

    // Return the cached funds if no API call is required
    return Promise.resolve(fundsData);
  };

/**
 * Caches the provided funds data by dispatching an action to get data for multiple funds.
 *
 * Note : This is a little bit unintuitive, but we do this call here to pre-fetch the fund data
 * that we do not have in cache, and put it in our cache, so the subsequent calls to getFundData
 * won't call the server, but will get the data from the cache
 *
 * @param {Fund[]} funds - An array of Fund objects to be cached.
 * @returns {AsyncResult<void>} A function that dispatches an action to get data for the provided funds.
 */
export const cacheFundsData =
  (funds: Fund[]): AsyncResult<void> =>
  async dispatch => {
    await dispatch(getManyFundsData(funds.map(f => f._id)));
  };
